import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  verifyAdminPassword, 
  createAdminSession, 
  verifyAdminSession, 
  clearAdminSession,
  loginRateLimiter 
} from '../utils/adminAuth';
import { sanitizeFormData, containsMaliciousContent } from '../utils/security';

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [subscriberCount, setSubscriberCount] = useState<number>(0);
  const [healthHackData, setHealthHackData] = useState({
    subject: '',
    excerpt: '',
    content: '',
    category: 'wellness',
    tags: '',
    featured: false
  });
  const [isCreating, setIsCreating] = useState(false);
  const [status, setStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [existingTips, setExistingTips] = useState<any[]>([]);
  const [isLoadingTips, setIsLoadingTips] = useState(false);

  const categories = [
    { id: 'stress-relief', name: 'Stress Relief', color: 'bg-red-100 text-red-800' },
    { id: 'mindfulness', name: 'Mindfulness', color: 'bg-purple-100 text-purple-800' },
    { id: 'sleep', name: 'Sleep Better', color: 'bg-blue-100 text-blue-800' },
    { id: 'self-care', name: 'Self Care', color: 'bg-pink-100 text-pink-800' },
    { id: 'wellness', name: 'Wellness', color: 'bg-green-100 text-green-800' }
  ];

  useEffect(() => {
    // Check if already authenticated
    if (verifyAdminSession()) {
      setIsAuthenticated(true);
      fetchSubscriberCount();
      fetchExistingTips();
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting for login attempts
    const clientId = 'admin_login'; // In production, use IP address
    if (!loginRateLimiter.isAllowed(clientId)) {
      setLoginError('Too many login attempts. Please try again in 15 minutes.');
      return;
    }

    try {
      const isValid = await verifyAdminPassword(password);
      if (isValid) {
        setIsAuthenticated(true);
        createAdminSession();
        setLoginError('');
        fetchSubscriberCount();
        loginRateLimiter.reset(clientId);
      } else {
        setLoginError('Incorrect password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Authentication error. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearAdminSession();
  };

  const fetchSubscriberCount = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5001/api/newsletter/subscribers'
        : '/api/newsletter/subscribers';

      const response = await fetch(apiUrl);
      const result = await response.json();

      if (result.success) {
        setSubscriberCount(result.count);
      }
    } catch (error) {
      console.error('Failed to fetch subscriber count:', error);
    }
  };

  const fetchExistingTips = async () => {
    setIsLoadingTips(true);
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5001/api/daily-tips'
        : '/api/daily-tips';

      const response = await fetch(apiUrl);
      const result = await response.json();

      if (result.success && result.tips) {
        setExistingTips(result.tips.slice(0, 20)); // Show last 20 tips
      }
    } catch (error) {
      console.error('Failed to fetch existing tips:', error);
    } finally {
      setIsLoadingTips(false);
    }
  };

  const toggleFeaturedStatus = async (tipId: number, currentFeatured: boolean) => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5001/api/daily-tips'
        : '/api/daily-tips';

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: tipId,
          featured: !currentFeatured
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          type: 'success',
          message: `Tip ${!currentFeatured ? 'featured' : 'unfeatured'} successfully!`
        });
        // Refresh the tips list
        fetchExistingTips();
      } else {
        setStatus({
          type: 'error',
          message: result.message || 'Failed to update tip'
        });
      }
    } catch (error) {
      console.error('Failed to toggle featured status:', error);
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    }
  };

  const handleCreateHealthHack = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setStatus({ type: null, message: '' });

    try {
      // Sanitize form data
      const sanitizedData = sanitizeFormData(healthHackData);
      
      // Check for malicious content
      const allContent = Object.values(sanitizedData).join(' ');
      if (containsMaliciousContent(allContent)) {
        setStatus({
          type: 'error',
          message: 'Invalid content detected. Please check your input and try again.'
        });
        setIsCreating(false);
        return;
      }

      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5001/api/daily-tips'
        : '/api/daily-tips';

      // Parse tags from comma-separated string
      const tagsArray = sanitizedData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: sanitizedData.subject,
          excerpt: sanitizedData.excerpt,
          content: sanitizedData.content,
          category: sanitizedData.category,
          tags: tagsArray,
          featured: healthHackData.featured
        }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          type: 'success',
          message: 'Health hack created successfully!'
        });
        // Reset form
        setHealthHackData({
          subject: '',
          excerpt: '',
          content: '',
          category: 'wellness',
          tags: '',
          featured: false
        });
      } else {
        setStatus({
          type: 'error',
          message: result.message || 'Failed to create health hack'
        });
      }
    } catch (error) {
      console.error('Create health hack error:', error);
      setStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <title>Admin Login - Destress Daily</title>
          <meta name="description" content="Admin login for Destress Daily" />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 bg-gradient-to-r from-health-500 to-primary-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">DH</span>
              </div>
              <h2 className="mt-6 text-3xl font-bold text-gray-900">
                Admin Login
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Enter password to access admin dashboard
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                {loginError && (
                  <div className="text-red-600 text-sm">
                    {loginError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full btn-primary py-3 text-lg"
                >
                  Login
                </button>
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Admin dashboard
  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Destress Daily</title>
        <meta name="description" content="Admin dashboard for Destress Daily" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header with logout */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-8">
                Destress Daily
              </h1>
              <p className="text-lg text-gray-700 mb-6">
                Send daily stress relief tips to your newsletter subscribers and post them to your website
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="btn-secondary"
            >
              Logout
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Total Subscribers
              </h3>
              <p className="text-3xl font-bold text-primary-600">
                {subscriberCount}
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Destress Daily
              </h3>
              <p className="text-3xl font-bold text-health-600">
                Auto-Posted
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Website Content
              </h3>
              <p className="text-3xl font-bold text-gray-600">
                Updated
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Create Daily Tip */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Create New Daily Tip
              </h2>
              
              <form onSubmit={handleCreateHealthHack} className="space-y-6">
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Daily Tip Title
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={healthHackData.subject}
                    onChange={(e) => setHealthHackData({ ...healthHackData, subject: e.target.value })}
                    placeholder="e.g., The 5-Minute Morning Routine That Boosts Your Energy"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-2">
                    Daily Tip Excerpt (HTML)
                  </label>
                  <textarea
                    id="excerpt"
                    value={healthHackData.excerpt}
                    onChange={(e) => setHealthHackData({ ...healthHackData, excerpt: e.target.value })}
                    placeholder="Write your daily tip excerpt in HTML format..."
                    rows={4}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                    Daily Tip Content (HTML)
                  </label>
                  <textarea
                    id="content"
                    value={healthHackData.content}
                    onChange={(e) => setHealthHackData({ ...healthHackData, content: e.target.value })}
                    placeholder="Write your daily tip content in HTML format..."
                    rows={8}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      id="category"
                      value={healthHackData.category}
                      onChange={(e) => setHealthHackData({ ...healthHackData, category: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      id="tags"
                      value={healthHackData.tags}
                      onChange={(e) => setHealthHackData({ ...healthHackData, tags: e.target.value })}
                      placeholder="e.g., energy, morning routine, wellness"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={healthHackData.featured}
                    onChange={(e) => setHealthHackData({ ...healthHackData, featured: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                    Feature this hack on the homepage
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isCreating}
                  className="w-full btn-primary"
                >
                  {isCreating ? 'Creating...' : 'Create Health Hack'}
                </button>

                {status.type && (
                  <div className={`p-4 rounded-lg ${
                    status.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {status.message}
                  </div>
                )}
              </form>
            </div>

            {/* Manage Existing Tips */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Manage Existing Tips
              </h2>
              
              {isLoadingTips ? (
                <div className="text-center py-8">
                  <div className="loading-spinner h-8 w-8 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading tips...</p>
                </div>
              ) : existingTips.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {existingTips.map((tip) => (
                    <div key={tip.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">
                          {tip.title}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          tip.featured 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tip.featured ? 'Featured' : 'Not Featured'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span>Category: {tip.category}</span>
                        <span>{tip.read_time} min read</span>
                      </div>
                      
                      <button
                        onClick={() => toggleFeaturedStatus(tip.id, tip.featured)}
                        className={`text-xs px-3 py-1 rounded border transition-colors ${
                          tip.featured
                            ? 'border-red-300 text-red-600 hover:bg-red-50'
                            : 'border-green-300 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {tip.featured ? 'Remove from Featured' : 'Add to Featured'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No tips found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPage; 